import { useMemo } from "react";
import {
  toISO,
  fromISO,
  MONTH_SHORT,
  DOW_SHORT,
} from "@/lib/dates";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Absence Calendar Heatmap.
 * Renders one month grid. Each cell = a date with:
 *   - the day number
 *   - a colour whose intensity encodes the total absence-count that day
 *   - a mini badge with H (holiday) / S (sickness) / A (absence) counts
 * Clicking a day calls onSelectDate(iso).
 */
export default function AbsenceHeatmap({
  monthDate,
  onMonthChange,
  absences,
  onSelectDate,
  selectedDate,
}) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const daysGrid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // 6 rows x 7 cols
    const cells = [];
    // Leading blanks
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  // Count absences per date per type
  const byDate = useMemo(() => {
    const m = {}; // iso -> {holiday, sickness, absence, total}
    for (const ab of absences) {
      let cur = fromISO(ab.start_date);
      const end = fromISO(ab.end_date);
      while (cur <= end) {
        const key = toISO(cur);
        if (!m[key]) m[key] = { holiday: 0, sickness: 0, absence: 0, total: 0 };
        m[key][ab.type] = (m[key][ab.type] || 0) + 1;
        m[key].total += 1;
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [absences]);

  const maxTotal = useMemo(() => {
    let mx = 0;
    Object.values(byDate).forEach((v) => {
      if (v.total > mx) mx = v.total;
    });
    return mx;
  }, [byDate]);

  const intensityBg = (total) => {
    if (!total) return "bg-white";
    const t = maxTotal > 0 ? total / maxTotal : 0;
    if (t > 0.75) return "bg-rose-500";
    if (t > 0.5) return "bg-rose-400";
    if (t > 0.25) return "bg-amber-300";
    return "bg-amber-100";
  };
  const intensityText = (total) => {
    if (!total) return "text-zinc-900";
    const t = maxTotal > 0 ? total / maxTotal : 0;
    return t > 0.5 ? "text-white" : "text-zinc-900";
  };

  const goto = (delta) => {
    const nd = new Date(year, month + delta, 1);
    onMonthChange(nd);
  };

  const legendItems = [
    { label: "H", type: "holiday", color: "bg-amber-400" },
    { label: "S", type: "sickness", color: "bg-rose-500" },
    { label: "A", type: "absence", color: "bg-zinc-700" },
  ];

  return (
    <div className="border-2 border-black bg-white" data-testid="absence-heatmap">
      <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
        <div className="font-heading font-black text-lg tracking-tight leading-none">
          {MONTH_SHORT[month]} {year}
        </div>
        <div className="flex items-center gap-3">
          {selectedDate && (
            <button
              onClick={() => onSelectDate(null)}
              data-testid="clear-date-filter"
              className="text-[10px] font-mono-plex tracking-widest underline hover:no-underline"
            >
              CLEAR DATE FILTER
            </button>
          )}
          <div className="flex items-center gap-3 text-[10px] font-mono-plex tracking-widest text-zinc-600">
            {legendItems.map((l) => (
              <div key={l.type} className="flex items-center gap-1">
                <span className={`w-2.5 h-2.5 ${l.color} inline-block`} />
                {l.label}
              </div>
            ))}
          </div>
          <div className="flex border border-black">
            <button
              onClick={() => goto(-1)}
              data-testid="heatmap-prev"
              className="px-2 h-8 border-r border-black hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <button
              onClick={() => onMonthChange(new Date(2026, 3, 1))}
              className="px-3 h-8 border-r border-black hover:bg-zinc-100 text-[10px] font-mono-plex tracking-widest font-semibold"
            >
              APR 26
            </button>
            <button
              onClick={() => goto(1)}
              data-testid="heatmap-next"
              className="px-2 h-8 hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-zinc-300 bg-zinc-50">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-[10px] font-mono-plex tracking-widest text-zinc-500 border-r border-zinc-200 last:border-r-0 text-center"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7">
        {daysGrid.map((d, i) => {
          if (!d)
            return (
              <div
                key={`empty-${i}`}
                className="border-r border-b border-zinc-200 last-in-row:border-r-0 h-20 bg-zinc-50"
              />
            );
          const iso = toISO(d);
          const stats = byDate[iso] || { holiday: 0, sickness: 0, absence: 0, total: 0 };
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isSelected = selectedDate === iso;
          return (
            <button
              key={iso}
              onClick={() => onSelectDate(iso === selectedDate ? null : iso)}
              data-testid={`heatmap-cell-${iso}`}
              className={`border-r border-b border-zinc-200 h-20 p-1.5 flex flex-col text-left transition-all ${intensityBg(
                stats.total
              )} ${intensityText(stats.total)} hover:outline hover:outline-2 hover:outline-black ${
                isSelected ? "outline outline-2 outline-black z-10" : ""
              } ${isWeekend && stats.total === 0 ? "bg-zinc-100/60" : ""}`}
            >
              <div className="flex items-center justify-between w-full">
                <span className="font-heading font-bold text-base leading-none">{d.getDate()}</span>
                {stats.total > 0 && (
                  <span className="font-mono-plex text-[9px] tracking-widest font-bold">
                    {stats.total}
                  </span>
                )}
              </div>
              {stats.total > 0 && (
                <div className="mt-auto flex items-center gap-0.5 flex-wrap">
                  {stats.holiday > 0 && (
                    <span className="bg-amber-400 text-amber-950 text-[8px] font-mono-plex tracking-widest px-1 py-0.5 leading-none font-bold">
                      H{stats.holiday}
                    </span>
                  )}
                  {stats.sickness > 0 && (
                    <span className="bg-rose-500 text-white text-[8px] font-mono-plex tracking-widest px-1 py-0.5 leading-none font-bold">
                      S{stats.sickness}
                    </span>
                  )}
                  {stats.absence > 0 && (
                    <span className="bg-zinc-700 text-white text-[8px] font-mono-plex tracking-widest px-1 py-0.5 leading-none font-bold">
                      A{stats.absence}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
